import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@pilot-hub/auth'
import { AppShell, NavIcons, NAV_ICON_CLASS, UserMenuIcons, StatusBar, StaleBanner, useFeedbackUngelesen, useMissionContext } from '@pilot-hub/ui'
import type { NavItem, UserMenuEntry, PaletteAction } from '@pilot-hub/ui'
import { missionApi } from '@pilot-hub/api-client'
import { isFeatureEnabled } from '@pilot-hub/config'


/**
 * Admin-Modul auf der gemeinsamen AppShell (packages/ui/src/components/shell).
 * Gruppen: Betrieb / Material / Verwaltung. Einstellungen und Feedback über Benutzermenü und Befehlspalette.
 */
export function Layout() {
  const { user, logout } = useAuth()
  const { activeMission } = useMissionContext()
  const location = useLocation()
  const feedbackUngelesen = useFeedbackUngelesen()

  const navigate = useNavigate()
  const darfVerwalten = !!user?.rollen?.some(r => r === 'Admin' || r === 'Führungskraft' || r === 'ADMIN' || r === 'FUEHRUNG')
  const istAdmin = !!user?.rollen?.some(r => r === 'Admin' || r === 'ADMIN')

  // Befehlspalette (Strg+K): „Neu …“ und „Einsatz öffnen: …“ — Navigationsmenü bleibt unverändert
  const [einsaetzeKurz, setEinsaetzeKurz] = useState<{ id: number; titel: string; einsatznummer?: string | null }[]>([])
  useEffect(() => {
    if (!darfVerwalten) return
    let weg = false
    missionApi.list({ size: 500 })
      .then((r: any) => { if (!weg) setEinsaetzeKurz(((r?.data?.items ?? r?.items ?? []) as any[]).slice(0, 60)) })
      .catch(() => { /* ohne Einsatzliste nur ohne „Einsatz öffnen“ */ })
    return () => { weg = true }
  }, [darfVerwalten])
  const paletteActions: PaletteAction[] = useMemo(() => [
    ...(darfVerwalten ? [
      { id: 'neu-drohne', label: 'Neue Drohne', group: 'Aktion', keywords: 'anlegen hinzufügen drohne', run: () => navigate('/drones?neu=1') },
      { id: 'neu-akku', label: 'Neuer Akku', group: 'Aktion', keywords: 'anlegen hinzufügen akku', run: () => navigate('/akkus?neu=1') },
      { id: 'neu-einsatz', label: 'Neuer Einsatz', group: 'Aktion', keywords: 'anlegen einsatz übung', run: () => navigate('/missions?neu=1') },
    ] : []),
    ...(istAdmin ? [{ id: 'neu-benutzer', label: 'Neuer Benutzer', group: 'Aktion', keywords: 'anlegen hinzufügen nutzer user', run: () => navigate('/users?neu=1') }] : []),
    ...einsaetzeKurz.map(e => ({
      id: `einsatz-${e.id}`, label: `Einsatz öffnen: ${e.titel}`, group: 'Einsatz', keywords: `${e.einsatznummer ?? ''} einsatz öffnen`,
      run: () => navigate(`/missions?oeffnen=${e.id}`),
    })),
  ], [darfVerwalten, istAdmin, einsaetzeKurz, navigate])

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', group: 'Betrieb', dock: true, icon: <NavIcons.dashboard className={NAV_ICON_CLASS} /> },
    { path: '/missions', label: 'Einsätze', group: 'Betrieb', dock: true, icon: <NavIcons.missions className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'missions') },
    { path: '/flights', label: 'Flüge', group: 'Betrieb', dock: true, icon: <NavIcons.flights className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'flights') },
    { path: '/medien', label: 'Medien', group: 'Betrieb', icon: <NavIcons.media className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'] },
    { path: '/checklisten', label: 'Checklisten', group: 'Betrieb', icon: <NavIcons.checklist className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'checklisten') },
    { path: '/drones', label: 'Drohnen', group: 'Material', dock: true, icon: <NavIcons.drones className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'drones') },
    { path: '/akkus', label: 'Akkus', group: 'Material', icon: <NavIcons.akkus className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'akkus') },
    { path: '/vehicles', label: 'Fahrzeuge', group: 'Material', icon: <NavIcons.vehicles className={NAV_ICON_CLASS} />, roles: ['Admin', 'Führungskraft'], show: isFeatureEnabled('admin', 'vehicles') },
    { path: '/users', label: 'Benutzer', group: 'Verwaltung', icon: <NavIcons.users className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'users') },
    { path: '/licenses', label: 'Lizenzen', group: 'Verwaltung', icon: <NavIcons.licenses className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'licenses') },
    { path: '/export', label: 'Export', group: 'Verwaltung', icon: <NavIcons.export className={NAV_ICON_CLASS} />, roles: ['Admin'], show: isFeatureEnabled('admin', 'export') },
  ]

  const userMenu: UserMenuEntry[] = useMemo(() => [
    { id: 'settings', label: 'Einstellungen', icon: UserMenuIcons.settings, to: '/settings' },
    {
      id: 'feedback', label: 'Feedback & Roadmap', icon: UserMenuIcons.feedback, to: '/feedback',
      state: { von: location.pathname + location.search }, badge: feedbackUngelesen,
    },
    { type: 'heading', label: 'Handbuch' },
    { id: 'hb-kompakt', label: 'Handbuch kompakt', icon: UserMenuIcons.handbookShort, href: '/handbuch/anwenderhandbuch-admin-kompakt.pdf' },
    { id: 'hb-lang', label: 'Handbuch ausführlich', icon: UserMenuIcons.handbookLong, href: '/handbuch/anwenderhandbuch-admin-ausfuehrlich.pdf' },
    { type: 'separator' },
    { id: 'logout', label: 'Abmelden', icon: UserMenuIcons.logout, danger: true, onSelect: () => logout() },
  ], [location.pathname, location.search, feedbackUngelesen, logout])

  return (
    <AppShell
      moduleName="Admin-Modul"
      moduleSubtitle="ASB Drohnenstaffel"
      logoSrc="/logo.png"
      navItems={navItems}
      user={{ name: `${user?.vorname ?? ''} ${user?.nachname ?? ''}`.trim() || 'Benutzer', username: user?.benutzername, subtitle: (user?.rollen?.[0] as string | undefined), dot: feedbackUngelesen > 0 }}
      userMenu={userMenu}
      paletteActions={paletteActions}
      status={<StatusBar />}
      banner={<StaleBanner />}
      headerLeft={activeMission ? (
        <div className="hidden items-center gap-2 rounded-full border border-primary-600/30 bg-primary-600/10 px-3 py-1 text-sm text-primary-200 md:flex">
          📍 {activeMission.title}
        </div>
      ) : undefined}
    />
  )
}
